<template>
  <ib-application setupSSO>
    <router-view v-slot="{ Component }">
      <div :class="{ 'min-h-[90vh]': !Component }">
        <component :is="Component" />
      </div>
    </router-view>
  </ib-application>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import { useUserState } from '@/stores/useUserState';
import { usePositionsManager } from '@/composables/usePositionsManager';
import { useCommonNativeActions } from '@/composables/useCommonNativeActions';
import { isOrderTicketPending } from '@/electionHub/composables/useOrderTicket';

const moduleProps = inject<{
  authenticated: boolean;
  accountId?: string;
  routePath?: string;
}>('moduleProps', {
  authenticated: false,
  accountId: '',
});

const moduleEmit = inject<(event: string, ...args: unknown[]) => void>('moduleEmit', () => {});

const route = useRoute();
const userStateStore = useUserState();

const resolvedAccount = computed(() => moduleProps.accountId || (route.query.account as string | undefined) || '');
const resolvedAuthenticated = computed(() => moduleProps.authenticated || !!route.query.account);

watchEffect(() => {
  userStateStore.setUserState(resolvedAuthenticated.value, resolvedAccount.value);
});

const { positionsStore } = usePositionsManager(resolvedAccount);

useCommonNativeActions();

const bridge = app.getBridge();
const unsubscribeBridge = bridge?.onMessage((msg) => {
  if (msg.action === 'order_occurred') {
    positionsStore.refreshPositions();
  }
});

const router = useRouter();

watch(
  () => moduleProps.routePath,
  (fullPath) => {
    if (!fullPath || fullPath === route.fullPath || isOrderTicketPending()) return;
    router.replace(fullPath).catch((error) => console.error('Host route synchronization failed', error));
  },
  { immediate: true },
);

watch(
  () => route.path,
  (path) => {
    moduleEmit('route_change', { isMarkets: path === '/' });
  },
  { immediate: true },
);

let popstateInProgress = false;

const onPopstate = () => {
  popstateInProgress = true;
  requestAnimationFrame(() => {
    popstateInProgress = false;
  });
};

const onHashChange = () => {
  if (popstateInProgress) return;
  if (isOrderTicketPending()) return;
  const hashPath = window.location.hash.slice(1) || '/';
  if (hashPath !== route.fullPath) {
    router.replace(hashPath);
  }
};

onMounted(() => {
  window.addEventListener('popstate', onPopstate);
  window.addEventListener('hashchange', onHashChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopstate);
  window.removeEventListener('hashchange', onHashChange);
  unsubscribeBridge?.();
});
</script>
<!-- last modified by AI tools on 2026-10-07 -->
